/* Atlas v3 — Overview hero chart.
   The real TradingView lightweight-charts area chart for the equal-weight
   Atlas index (atlas_index → useAtlasIndex().levels). Replaces the old
   stylized SVG squiggle + starfield-clip. Same library / look as the
   ticker drawer + portfolio chart (already in the bundle, ~0 extra cost).

   Props:
     levels  — [{ date:'YYYY-MM-DD', level:Number }] (already sliced to the
               selected timeframe by the caller)
     color   — '--studio-success' | '--studio-danger' token name (line/fill)
     height  — px (default 150)

   Crosshair scrub + pulsing last-point dot. Reduced-motion drops the pulse. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createChart, AreaSeries, LineStyle, CrosshairMode } from 'lightweight-charts'
import { REDUCE } from './lib'

// Resolve an --studio-* HSL-triplet token to a concrete hsl() string the
// chart lib can consume. el inherits [data-theme] so night theme works too.
function tok(el, name, alpha) {
  const v = getComputedStyle(el).getPropertyValue(name).trim()
  if (!v) return alpha != null ? `rgba(0,0,0,0)` : '#888'
  return alpha != null ? `hsl(${v} / ${alpha})` : `hsl(${v})`
}

function fmtDate(d) {
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return '' }
}

// Accepts EITHER `levels` ([{date:'YYYY-MM-DD', level}]) for the daily
// atlas-index, OR `points` ([{t:ms, value}]) for the real intraday/multi-range
// chart-bars composite. The hover tooltip derives its date from the timestamp,
// so intraday points render fine too.
// `pulse` (default true) animates the last-point dot; pass false to render it
// static (wired to the live_pulse_enabled pref on the V3 desktop).
// NOTE: no `= []` default on `levels` — a fresh array default changes identity
// every render, which sits in the `data` memo deps and loops the setDot effect
// ("Maximum update depth exceeded") whenever a caller passes only `points`.
export function HeroChart({ levels, points = null, color = '--studio-success', height = 150, intraday = false, onHover, pulse = true }) {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const seriesRef = useRef(null)
  const [dot, setDot] = useState(null)     // { x, y } | null
  // Keep the latest onHover in a ref so the once-per-mount crosshair handler
  // never goes stale (the headline % scrubs off this callback).
  const onHoverRef = useRef(onHover)
  onHoverRef.current = onHover
  // Device-local 12-hour time formatting for the axis + crosshair label. The
  // lib's DEFAULT formatters render UTC in 24-hour ("14:30") — wrong clock AND
  // wrong zone for every viewer. No timeZone pin: each device shows its own
  // local time (CT phone sees CT, a PT viewer sees PT). Ref so the
  // once-per-mount formatters track intraday flips without a chart rebuild.
  const intradayRef = useRef(intraday)
  intradayRef.current = intraday

  // Map levels → lightweight-charts shape, deduped + sorted (the lib throws
  // on duplicate or unsorted timestamps).
  const data = useMemo(() => {
    const src = points || levels || []
    const seen = new Set(), out = []
    for (const r of src) {
      const t = Number.isFinite(r.t)
        ? Math.floor(r.t / 1000)
        : Math.floor(new Date(r.date + 'T00:00:00').getTime() / 1000)
      const value = r.value != null ? r.value : r.level
      if (!Number.isFinite(t) || !Number.isFinite(Number(value)) || seen.has(t)) continue
      seen.add(t)
      out.push({ time: t, value: Number(value), date: r.date })
    }
    out.sort((a, b) => a.time - b.time)
    return out
  }, [levels, points])

  // Build the chart once per mount. Imperative API — reuse the instance.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      layout: { background: { type: 'solid', color: 'transparent' }, textColor: tok(el, '--studio-text-subtle'), fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, attributionLogo: false },
      // No gridlines — keep the area clean.
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      leftPriceScale: { visible: false, borderVisible: false },
      // Right % axis dropped (Ben: looks worse). Keep just the bottom time axis.
      rightPriceScale: { visible: false, borderVisible: false, scaleMargins: { top: 0.16, bottom: 0.12 } },
      timeScale: {
        visible: true, borderVisible: false, timeVisible: intraday, secondsVisible: false, fixLeftEdge: true, fixRightEdge: true,
        // Axis labels in the viewer's local timezone, 12-hour for intraday
        // ticks. TickMarkType: 0 Year, 1 Month, 2 Day, 3/4 Time.
        tickMarkFormatter: (t, type) => {
          const d = new Date(t * 1000)
          if (type === 0) return String(d.getFullYear())
          if (type === 1) return d.toLocaleDateString('en-US', { month: 'short' })
          if (type === 2) return String(d.getDate())
          return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        },
      },
      // % crosshair label still resolves (used by the value readout); no price axis to show it on.
      localization: {
        priceFormatter: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`,
        // Crosshair pill in the viewer's local timezone, 12-hour.
        timeFormatter: (t) => {
          const d = new Date(t * 1000)
          return intradayRef.current
            ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
            : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        // Accent-filled date/time label at the crosshair (was --studio-surface-2,
        // near-invisible on the dark chart) so scrubbing clearly shows the date.
        vertLine: { color: tok(el, '--studio-border', 0.9), width: 1, style: LineStyle.Dashed, labelVisible: true, labelBackgroundColor: tok(el, '--studio-accent') },
        horzLine: { color: tok(el, '--studio-border', 0.7), width: 1, style: LineStyle.Dashed, labelVisible: false },
      },
      handleScroll: false, handleScale: false, kineticScroll: { mouse: false, touch: false },
    })
    const series = chart.addSeries(AreaSeries, {
      lineWidth: 2.5, priceLineVisible: false, lastValueVisible: false,
      crosshairMarkerVisible: true, crosshairMarkerRadius: 4,
      crosshairMarkerBorderWidth: 2,
    })
    // Leave room top + bottom so the area isn't skeletal and the time axis has
    // breathing room under it.
    series.priceScale().applyOptions({ scaleMargins: { top: 0.16, bottom: 0.16 } })
    chartRef.current = chart
    seriesRef.current = series

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) if (e.contentRect.width > 0) chart.applyOptions({ width: e.contentRect.width })
    })
    ro.observe(el)

    const unsub = chart.subscribeCrosshairMove((p) => {
      if (!p.point || !p.time || !p.seriesData?.size) { onHoverRef.current?.(null); return }
      const d = p.seriesData.get(series)
      onHoverRef.current?.({ value: d?.value, time: Number(p.time) })
    })

    return () => { unsub && unsub(); ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height])

  // Push data + direction color on every change.
  useEffect(() => {
    const el = containerRef.current
    const chart = chartRef.current, series = seriesRef.current
    if (!el || !chart || !series) return
    if (data.length < 2) { series.setData([]); setDot(null); return }
    const line = tok(el, color)
    series.applyOptions({
      lineColor: line, topColor: tok(el, color, 0.34), bottomColor: tok(el, color, 0.02),
      crosshairMarkerBackgroundColor: line, crosshairMarkerBorderColor: tok(el, '--studio-background'),
    })
    series.setData(data.map((d) => ({ time: d.time, value: d.value })))
    chart.timeScale().fitContent()
    requestAnimationFrame(() => {
      if (!chartRef.current || !seriesRef.current) return
      const last = data[data.length - 1]
      const x = chart.timeScale().timeToCoordinate(last.time)
      const y = series.priceToCoordinate(last.value)
      if (x != null && y != null) setDot({ x, y })
    })
  }, [data, color])

  // Toggle intraday time labels (hours vs dates) without rebuilding the chart.
  useEffect(() => {
    chartRef.current?.applyOptions({ timeScale: { timeVisible: intraday } })
  }, [intraday])

  const dotColor = containerRef.current ? tok(containerRef.current, color) : '#3fb950'

  return (
    <div ref={containerRef} onMouseLeave={() => onHoverRef.current?.(null)}
      style={{ position: 'relative', width: '100%', height }}>
      {dot && (
        <span style={{
          position: 'absolute', left: dot.x, top: dot.y, width: 8, height: 8, borderRadius: '50%',
          background: dotColor, color: dotColor, transform: 'translate(-50%,-50%)', pointerEvents: 'none',
          boxShadow: `0 0 8px ${dotColor}`, zIndex: 2,
          animation: (REDUCE || !pulse) ? 'none' : 'pcPulseDot 1.8s ease-in-out infinite',
        }} />
      )}
      {/* No floating tooltip — the hovered value/date scrubs the headline %
          above the chart (reported via onHover). The dashed crosshair line +
          marker dot are the on-chart indicators. */}
      <style>{'@keyframes pcPulseDot{0%,100%{opacity:1;transform:translate(-50%,-50%) scale(1)}50%{opacity:.45;transform:translate(-50%,-50%) scale(1.6)}}'}</style>
    </div>
  )
}
